import { createContext, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { CustomerData } from 'types/api/customer.type';
import { MessageType } from 'types/api/message.type';

import { useAppSelector } from 'redux/hooks';

import { MessagesDataService } from 'services/messages.service';

type ProviderProps = {
  children: React.ReactNode;
};

export type Chat = {
  messages: MessageType[] | [];
  messagesSet: (arg: MessageType[] | []) => void;
  isVisible: boolean;
  isVisibleSet: (arg: boolean) => void;
  isRestoreRequest: boolean;
  isRestoreRequestSet: (arg: boolean) => void;
  customerData?: CustomerData;
  customerDataSet: (arg: CustomerData) => void;
  send: (msg: string) => void;
  isSent: boolean;
  setIsSent: (arg: boolean) => void;
};

const defaultState: Chat = {
  messages: [],
  messagesSet: () => {},
  isVisible: false,
  customerData: undefined,
  isVisibleSet: () => {},
  isRestoreRequest: false,
  isRestoreRequestSet: () => {},
  send: () => {},
  customerDataSet: () => {},
  isSent: false,
  setIsSent: () => {}
};

export const ChatContext = createContext(defaultState);

// 28.08.2026: hidden (not removed — code stays, flip to re-enable).
// The chat feature calls REST/WebSocket endpoints (`/v1/messages/*`,
// `/chat`) that don't exist on the current NestJS backend at all (legacy
// Java-backend feature, never migrated) — every customer/shop page was
// silently spamming 404s in the background. Possible future use.
const CHAT_ENABLED = false;

export const ChatProvider: React.FunctionComponent<ProviderProps> = ({ children }) => {
  const user = useAppSelector((state) => state.userData.user);

  const [customerData, customerDataSet] = useState<CustomerData>();
  const [messages, messagesSet] = useState<MessageType[] | []>([]);
  const [isVisible, isVisibleSet] = useState<boolean>(false);
  const [isRestoreRequest, isRestoreRequestSet] = useState<boolean>(false);
  const [isSent, setIsSent] = useState<boolean>(false);

  const messagesService =
    user && customerData && customerData.companyName
      ? new MessagesDataService(
          {
            id: customerData.id ? customerData.id : customerData.code,
            name: customerData.companyName,
          },
          {
            id: user.id,
            name: user.username,
          }
        )
      : undefined;

  const getAndSetAllMessages = () =>
    messagesService?.fetchAllMessages().then((res) => {
      const messagesSorted =
        res.data.content.sort(
          (a: MessageType, b: MessageType) =>
            dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
        ) || [];

      messagesSet(messagesSorted);
    });

  const send = (message: string) => {
    messagesService?.send(message);
  };

  const getLatestMessage = (message: MessageType) => {
    messagesSet((state) => [...state, message]);
    setTimeout(getAndSetAllMessages, 200);
  };

  const state: Chat = useMemo(
    () => ({
      messages,
      isVisible,
      customerData,
      isVisibleSet,
      send,
      customerDataSet,
      messagesSet,
      isRestoreRequest,
      isRestoreRequestSet,
      isSent,
      setIsSent
    }),
    [messages, isVisible, customerData, isRestoreRequest, isSent, setIsSent]
  );

  useEffect(() => {
    if (CHAT_ENABLED && customerData && user) {
      messagesService?.init(getLatestMessage);

      getAndSetAllMessages();
    }
  }, [customerData]);

  return <ChatContext.Provider value={state}>{children}</ChatContext.Provider>;
};
